import React, { useCallback, useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { formatDate } from "@/lib/formatDate";
import { useNavigate } from "react-router-dom";
import { apiClient, logoutRequest } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Clock, Play, Square, Plane, LogOut } from "lucide-react";
import { PortalReceiptsCard } from "@/components/PortalReceiptsCard";
import { Toaster } from "@/components/ui/sonner";

const ABSENCE_LABELS = { vacation: "Ferien", sick: "Krankheit", military: "Militär", accident: "Unfall", unpaid: "Unbezahlter Urlaub", other: "Andere" };
const STATUS_BADGE = {
  requested: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};
const STATUS_LABEL = { requested: "offen", approved: "genehmigt", rejected: "abgelehnt" };

export default function MyTime() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [absData, setAbsData] = useState(null);
  const [breakMin, setBreakMin] = useState("30");
  const [absForm, setAbsForm] = useState({ type: "vacation", start_date: "", end_date: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const p = await apiClient.get("/my/portal/profile");
      if (!p.data.is_employee) { navigate("/"); return; }
      setProfile(p.data);
      const [s, e, a] = await Promise.all([
        apiClient.get("/my/portal/status"),
        apiClient.get("/my/portal/entries", { params: { month } }),
        apiClient.get("/my/portal/absences", { params: { year: month.slice(0, 4) } }),
      ]);
      setStatus(s.data); setData(e.data); setAbsData(a.data);
    } catch { toast.error("Laden fehlgeschlagen"); }
  }, [month, navigate]);

  useEffect(() => { load(); }, [load]);

  const clock = async (action) => {
    setBusy(true);
    try {
      if (action === "in") { await apiClient.post("/my/portal/clock-in"); toast.success("Eingestempelt"); }
      else {
        const r = await apiClient.post("/my/portal/clock-out", { break_minutes: Number(breakMin) || 0 });
        toast.success(`Ausgestempelt — ${r.data.hours} Std erfasst`);
      }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const requestAbsence = async (e) => {
    e.preventDefault();
    if (!absForm.start_date || !absForm.end_date) { toast.error("Zeitraum angeben"); return; }
    setBusy(true);
    try {
      await apiClient.post("/my/portal/absences", absForm);
      toast.success("Antrag eingereicht");
      setAbsForm({ type: "vacation", start_date: "", end_date: "", note: "" });
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const logout = async () => {
    await logoutRequest();
    window.location.assign("/login");
  };

  if (!profile) return <div className="min-h-screen bg-slate-50" />;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="mytime-page">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Meine Zeiterfassung</h1>
            <p className="text-sm text-slate-500">{profile.name} · {profile.company_name} · Pensum {profile.pensum_percent}%</p>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="mytime-logout"><LogOut className="h-4 w-4 mr-1" /> Abmelden</Button>
        </div>

        <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="mytime-clock-card">
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-3"><Clock className="h-4 w-4" /> Stempeluhr</h2>
          {status?.running ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5" data-testid="mytime-running-badge">
                Eingestempelt seit {status.entry.start_time}
              </span>
              <Input type="number" min="0" className="w-24" value={breakMin} onChange={(e) => setBreakMin(e.target.value)} data-testid="mytime-break-input" />
              <span className="text-xs text-slate-400">Min Pause</span>
              <Button disabled={busy} onClick={() => clock("out")} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="mytime-clock-out-btn">
                <Square className="h-4 w-4 mr-1" /> Ausstempeln
              </Button>
            </div>
          ) : (
            <Button disabled={busy} onClick={() => clock("in")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mytime-clock-in-btn">
              <Play className="h-4 w-4 mr-1" /> Einstempeln
            </Button>
          )}
        </Card>

        <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="mytime-month-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">Mein Monat</h2>
            <Input type="month" className="w-40" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="mytime-month-input" />
          </div>
          {data && (
            <div className="grid grid-cols-3 gap-3 mb-4 text-center">
              <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">Ist</p><p className="text-lg font-semibold">{data.ist_hours.toFixed(1)} h</p></div>
              <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">Soll</p><p className="text-lg font-semibold">{data.soll_hours.toFixed(1)} h</p></div>
              <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">Saldo</p><p className={`text-lg font-semibold ${data.saldo_hours >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{data.saldo_hours >= 0 ? "+" : ""}{data.saldo_hours.toFixed(1)} h</p></div>
            </div>
          )}
          <div className="max-h-64 overflow-y-auto">
            {(data?.entries || []).map((e) => (
              <div key={e.id} className="flex justify-between text-[13px] border-b border-slate-100 py-1.5" data-testid={`mytime-entry-${e.id}`}>
                <span className="text-slate-600">{formatDate(e.date)} · {e.start_time}–{e.end_time || "läuft"} {e.break_minutes ? `(${e.break_minutes}′ Pause)` : ""}</span>
                <span className="font-medium">{e.hours != null ? `${e.hours.toFixed(2)} h` : "…"}</span>
              </div>
            ))}
            {(data?.entries || []).length === 0 && <p className="text-sm text-slate-400">Keine Einträge.</p>}
          </div>
        </Card>

        <PortalReceiptsCard />
        <Toaster position="top-right" richColors />

        <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="mytime-absence-card">
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-1"><Plane className="h-4 w-4" /> Ferien & Absenzen</h2>
          {absData?.vacation && (
            <p className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-md px-2.5 py-1.5 inline-block mb-3" data-testid="mytime-vacation-balance">
              Ferien-Saldo: {absData.vacation.remaining_days} von {absData.vacation.entitled_days} Tagen übrig
            </p>
          )}
          <form onSubmit={requestAbsence} className="flex flex-wrap items-center gap-2 text-sm mb-3">
            <select className="border border-slate-200 rounded-md px-2 py-1.5" value={absForm.type} onChange={(e) => setAbsForm({ ...absForm, type: e.target.value })} data-testid="mytime-absence-type">
              {Object.entries(ABSENCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <SwissDateField className="w-36" value={absForm.start_date} onChange={(e) => setAbsForm({ ...absForm, start_date: e.target.value })} data-testid="mytime-absence-start" />
            <SwissDateField className="w-36" value={absForm.end_date} onChange={(e) => setAbsForm({ ...absForm, end_date: e.target.value })} data-testid="mytime-absence-end" />
            <Button type="submit" size="sm" disabled={busy} data-testid="mytime-absence-save">Beantragen</Button>
          </form>
          {(absData?.absences || []).map((a) => (
            <div key={a.id} className="flex justify-between items-center text-[13px] border-b border-slate-100 py-1.5" data-testid={`mytime-absence-${a.id}`}>
              <span className="text-slate-600">{ABSENCE_LABELS[a.type]} · {formatDate(a.start_date)} → {formatDate(a.end_date)} ({a.days} Tage)</span>
              <span className={`text-[11px] border rounded-full px-2 py-0.5 ${STATUS_BADGE[a.status]}`}>{STATUS_LABEL[a.status]}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
